// libsodium-wrappers-sumo is ~1 MB (≈400 KB gzipped) and was previously a
// top-level import, so it landed in the eager bundle of *every* page (it is
// reached from the dashboard/admin layouts via the encryption store). It is
// only needed once a crypto operation actually runs (enroll, unlock, device
// ops), so we load it lazily here: the module-scope `sodium` binding is
// assigned inside initCrypto() via dynamic import, and every exported function
// already gates on `await initCrypto()` before touching `sodium` — so their
// bodies are unchanged. This keeps libsodium out of first paint.
//
// Type-only default import carries the full sodium surface for type-checking
// and is fully erased at build time (no runtime weight) — it matches the
// runtime `mod.default` we assign in initCrypto().
import type SodiumDefault from 'libsodium-wrappers-sumo';
import { LEGACY_PASSWORD_SHEET } from './recoverySchemes';

let sodium!: typeof SodiumDefault;

// Single-flight init: the first caller starts the import + sodium.ready; every
// concurrent caller awaits the same promise, so `sodium` is guaranteed assigned
// and ready by the time any `await initCrypto()` resolves. (No `initialized`
// flag — that left a window where two callers could both run the import.)
let initPromise: Promise<void> | null = null;

export function initCrypto(): Promise<void> {
  if (!initPromise) {
    initPromise = (async () => {
      const mod = await import('libsodium-wrappers-sumo');
      sodium = mod.default;
      await sodium.ready;
    })();
  }
  return initPromise;
}

/** Generate X25519 keypair for device enrollment. */
export async function generateDeviceKey(): Promise<{ publicKey: Uint8Array; privateKey: Uint8Array }> {
  await initCrypto();
  const kp = sodium.crypto_box_keypair();
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

/** Generate a random 256-bit key (Master KEK or DEK). */
export async function generateKey(): Promise<Uint8Array> {
  await initCrypto();
  return sodium.randombytes_buf(32);
}

/** Wrap Master KEK to a device's public key using NaCl box. */
export async function wrapMasterKEK(masterKEK: Uint8Array, recipientPubKey: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const ephKP = sodium.crypto_box_keypair();
  const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
  const sealed = sodium.crypto_box_easy(masterKEK, nonce, recipientPubKey, ephKP.privateKey);
  // Format: nonce(24) + ephPub(32) + ciphertext
  const result = new Uint8Array(nonce.length + ephKP.publicKey.length + sealed.length);
  result.set(nonce, 0);
  result.set(ephKP.publicKey, nonce.length);
  result.set(sealed, nonce.length + ephKP.publicKey.length);
  return result;
}

/** Unwrap Master KEK using device private key. */
export async function unwrapMasterKEK(wrapped: Uint8Array, privateKey: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const nonceLen = sodium.crypto_box_NONCEBYTES;  // 24
  const pubLen = sodium.crypto_box_PUBLICKEYBYTES; // 32
  const nonce = wrapped.slice(0, nonceLen);
  const senderPub = wrapped.slice(nonceLen, nonceLen + pubLen);
  const ciphertext = wrapped.slice(nonceLen + pubLen);
  return sodium.crypto_box_open_easy(ciphertext, nonce, senderPub, privateKey);
}

/**
 * Wrap DEK with Master KEK using XChaCha20-Poly1305-IETF.
 *
 * Both frontend (libsodium) and backend (golang.org/x/crypto/chacha20poly1305.NewX)
 * use XChaCha20-Poly1305 with 24-byte nonces. Format: nonce(24) + ciphertext.
 */
export async function wrapDEK(dek: Uint8Array, masterKEK: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(dek, null, null, nonce, masterKEK);
  const result = new Uint8Array(nonce.length + ciphertext.length);
  result.set(nonce, 0);
  result.set(ciphertext, nonce.length);
  return result;
}

/** Unwrap DEK with Master KEK. */
export async function unwrapDEK(wrapped: Uint8Array, masterKEK: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  const nonceLen = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;
  const nonce = wrapped.slice(0, nonceLen);
  const ciphertext = wrapped.slice(nonceLen);
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, null, nonce, masterKEK);
}

// The Argon2id params of the password sheet are frozen by registry row 0
// (recoverySchemes.ts), not read from libsodium: libsodium's MODERATE may be
// retuned by an upgrade, a printed sheet may not.

/** Derive master KEK from recovery password + stored recovery data. */
export async function unwrapWithRecoveryPassword(
  saltB64: string,
  wrappedB64: string,
  password: string,
): Promise<Uint8Array> {
  await initCrypto();
  const salt = Uint8Array.from(atob(saltB64), (c) => c.charCodeAt(0));
  const wrapped = Uint8Array.from(atob(wrappedB64), (c) => c.charCodeAt(0));
  const derivedKey = sodium.crypto_pwhash(
    LEGACY_PASSWORD_SHEET.kdf.keyBytes,
    password,
    salt,
    LEGACY_PASSWORD_SHEET.kdf.opslimit,
    LEGACY_PASSWORD_SHEET.kdf.memlimit,
    LEGACY_PASSWORD_SHEET.kdf.algId,
  );
  const nonceLen = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;
  const nonce = wrapped.slice(0, nonceLen);
  const ciphertext = wrapped.slice(nonceLen);
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, null, nonce, derivedKey);
}

/** Wrap Master KEK with recovery password via Argon2id + XChaCha20-Poly1305. */
export async function wrapWithRecoveryPassword(
  masterKEK: Uint8Array,
  password: string,
): Promise<{ salt: Uint8Array; wrapped: Uint8Array }> {
  await initCrypto();
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const derivedKey = sodium.crypto_pwhash(
    LEGACY_PASSWORD_SHEET.kdf.keyBytes,
    password,
    salt,
    LEGACY_PASSWORD_SHEET.kdf.opslimit,
    LEGACY_PASSWORD_SHEET.kdf.memlimit,
    LEGACY_PASSWORD_SHEET.kdf.algId,
  );
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(masterKEK, null, null, nonce, derivedKey);
  const wrapped = new Uint8Array(nonce.length + ciphertext.length);
  wrapped.set(nonce, 0);
  wrapped.set(ciphertext, nonce.length);
  return { salt, wrapped };
}

const SEED_BYTES = 32;

/**
 * crypto_box_seed_keypair(crypto_generichash(32, seed, utf8(label))).
 * A domain-separated seed→X25519 keypair for printed or PRF roots. The label is
 * a BLAKE2b key, so it must be 16..64 bytes; a 32-byte seed is required.
 */
export async function deriveSeedKeypair(
  seed: Uint8Array,
  label: string,
): Promise<{ publicKey: Uint8Array; privateKey: Uint8Array }> {
  await initCrypto();
  if (seed.length !== SEED_BYTES) {
    throw new Error(`deriveSeedKeypair: seed must be ${SEED_BYTES} bytes, got ${seed.length}`);
  }
  const key = sodium.from_string(label);
  if (key.length < sodium.crypto_generichash_KEYBYTES_MIN || key.length > sodium.crypto_generichash_KEYBYTES_MAX) {
    throw new Error(
      `deriveSeedKeypair: label must be ${sodium.crypto_generichash_KEYBYTES_MIN}..${sodium.crypto_generichash_KEYBYTES_MAX} bytes, got ${key.length}`,
    );
  }
  const x25519Seed = sodium.crypto_generichash(32, seed, key);
  const kp = sodium.crypto_box_seed_keypair(x25519Seed);
  sodium.memzero(x25519Seed);
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

/** Unkeyed BLAKE2b-256. */
export async function blake2b256(data: Uint8Array): Promise<Uint8Array> {
  await initCrypto();
  return sodium.crypto_generichash(32, data, null);
}

/** base64url without padding (libsodium URLSAFE_NO_PADDING). */
export async function toBase64Url(b: Uint8Array): Promise<string> {
  await initCrypto();
  return sodium.to_base64(b, sodium.base64_variants.URLSAFE_NO_PADDING);
}

/** Inverse of toBase64Url. Throws on a malformed payload. */
export async function fromBase64Url(s: string): Promise<Uint8Array> {
  await initCrypto();
  return sodium.from_base64(s, sodium.base64_variants.URLSAFE_NO_PADDING);
}
